<template>
  <div class="discount-vat">
    <!-- Quick periods -->
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Customer</label>
          <FormDropdown v-model="filters.customer" :options="customerOptions" placeholder="All customers" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Invoice</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Search invoice no. or customer" />
          </div>
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-discount-2"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Discount</div>
          <div class="rp-card-value">{{ formatMoney(summary.discount) }}</div>
          <div class="rp-card-sub">{{ summary.discount_rate }}% of sub-total · {{ plural(summary.discounted_count, 'invoice') }}</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-receipt-tax"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Taxable Sales</div>
          <div class="rp-card-value">{{ formatMoney(summary.taxable) }}</div>
          <div class="rp-card-sub">{{ summary.taxable_share }}% of sales · exempt {{ formatMoney(summary.exempt) }}</div>
        </div>
      </div>
      <div class="rp-card tone-teal">
        <span class="rp-card-icon"><i class="ti ti-building-bank"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">VAT Collected</div>
          <div class="rp-card-value">{{ formatMoney(summary.vat) }}</div>
          <div class="rp-card-sub">{{ summary.vat_rate }}% of taxable · {{ plural(summary.vat_count, 'invoice') }}</div>
        </div>
      </div>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-math-function"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Invoice Rounding</div>
          <div class="rp-card-value" :class="netClass(summary.rounding)">{{ signedMoney(summary.rounding) }}</div>
          <div class="rp-card-sub">
            {{ formatMoney(summary.rounding_up) }} rounded up · {{ formatMoney(Math.abs(Number(summary.rounding_down))) }} rounded down
          </div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Discount &amp; VAT {{ summary.trend_unit === 'month' ? 'by month' : 'by day' }}</span>
          <span class="rp-legend">
            <span><i class="rp-dot" :style="{ background: COUNT_COLOR }"></i>Discount</span>
            <span><i class="rp-dot" :style="{ background: VAT_COLOR }"></i>VAT</span>
          </span>
        </div>
        <div v-show="summary.trend.length" class="rp-chart"><canvas ref="trendChart"></canvas></div>
        <p v-if="!summary.trend.length" class="text-muted small mb-0">No sales in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">VAT by rate</div>
        <div v-if="rateBars.length" class="rp-bars">
          <div v-for="rate in rateBars" :key="rate.rate">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">
                <span class="rp-badge" :class="Number(rate.rate) ? 'info' : 'muted'">{{ Number(rate.rate) ? `${rate.rate}%` : 'Exempt' }}</span>
                <span class="text-muted ms-1">{{ plural(rate.lines, 'line') }}</span>
              </span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(rate.base) }}
                <span class="text-muted fw-normal">· VAT {{ formatMoney(rate.vat) }}</span>
              </span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :class="Number(rate.rate) ? 'info' : ''" :style="{ width: `${rate.percent}%` }"></div></div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No sales in this period.</p>
        <div class="rp-note mt-3">
          Sales after discount <strong>{{ formatMoney(summary.net_of_discount) }}</strong> = taxable
          {{ formatMoney(summary.taxable) }} + exempt {{ formatMoney(summary.exempt) }}
        </div>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Most discounted products</div>
        <ul v-if="summary.top_discounted.length" class="rp-ranked">
          <li v-for="(product, index) in summary.top_discounted" :key="product.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="product.name">{{ product.name }}</div>
              <small class="text-muted">{{ formatQty(product.qty) }} sold · {{ product.rate }}% off</small>
            </div>
            <span class="fw-semibold text-nowrap">{{ formatMoney(product.discount) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No discount was given in this period.</p>
      </div>
    </div>

    <!-- Invoices table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in showPills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: (filters.show || 'all') === pill.key }" @click="setShow(pill.key)">
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button type="button" class="rp-expand" :title="expanded[row.key] ? 'Hide items' : 'Show items'"
                          @click="toggleRow(row.key)">
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td class="text-nowrap">
                  {{ formatDate(row.date) }}
                  <span v-if="row.time" class="rp-sub">{{ row.time }}</span>
                </td>
                <td class="fw-semibold text-nowrap">{{ row.invoice_no }}</td>
                <td>{{ row.customer }}</td>
                <td class="text-end">{{ formatMoney(row.sub_total) }}</td>
                <td class="text-end">
                  <template v-if="Number(row.discount)">
                    −{{ formatMoney(row.discount) }}
                    <span class="rp-sub">{{ row.discount_pct }}%</span>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.taxable) }">
                  {{ Number(row.taxable) ? formatMoney(row.taxable) : '—' }}
                </td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.exempt) }">
                  {{ Number(row.exempt) ? formatMoney(row.exempt) : '—' }}
                </td>
                <td class="text-end">
                  <template v-if="Number(row.vat)">
                    {{ formatMoney(row.vat) }}
                    <span class="rp-sub">{{ row.vat_rate }}%</span>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-end" :class="Number(row.rounding) ? netClass(row.rounding) : 'text-muted'">
                  {{ Number(row.rounding) ? signedMoney(row.rounding) : '—' }}
                </td>
                <td class="text-end fw-semibold">{{ formatMoney(row.total) }}</td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title"><i class="ti ti-file-invoice me-1"></i>Invoice {{ row.invoice_no }}</div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Item Code</th>
                            <th>Item Name</th>
                            <th>Variant</th>
                            <th class="text-end">Qty</th>
                            <th class="text-end">Gross</th>
                            <th class="text-end">Discount</th>
                            <th class="text-end">After Discount</th>
                            <th class="text-end">VAT Rate</th>
                            <th class="text-end">VAT</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="line in row.lines" :key="line.key">
                            <td class="fw-semibold">{{ line.item_code }}</td>
                            <td>{{ line.item_name }}</td>
                            <td>
                              <template v-if="line.variant">
                                <span v-for="part in line.variant.split(' · ')" :key="part" class="rp-variant">{{ part }}</span>
                              </template>
                              <span v-else class="text-muted">—</span>
                            </td>
                            <td class="text-end">{{ formatQty(line.qty) }}<small v-if="line.unit" class="text-muted ms-1">{{ line.unit }}</small></td>
                            <td class="text-end">{{ formatMoney(line.gross) }}</td>
                            <td class="text-end">
                              {{ Number(line.discount) ? `−${formatMoney(line.discount)} (${line.discount_pct}%)` : '—' }}
                            </td>
                            <td class="text-end">{{ formatMoney(line.base) }}</td>
                            <td class="text-end">
                              <span class="rp-badge" :class="Number(line.vat_rate) ? 'info' : 'muted'">
                                {{ Number(line.vat_rate) ? `${line.vat_rate}%` : 'Exempt' }}
                              </span>
                            </td>
                            <td class="text-end fw-semibold">{{ Number(line.vat) ? formatMoney(line.vat) : '—' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-receipt-tax"></i>
                  <div class="fw-semibold">No invoices found</div>
                  <small class="text-muted">Try another period, customer or filter.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th></th>
              <th colspan="3" class="text-end">Total ({{ plural(rows.length, 'invoice') }})</th>
              <th class="text-end">{{ formatMoney(summary.sub_total) }}</th>
              <th class="text-end">{{ Number(summary.discount) ? `−${formatMoney(summary.discount)}` : '—' }}</th>
              <th class="text-end">{{ formatMoney(summary.taxable) }}</th>
              <th class="text-end">{{ formatMoney(summary.exempt) }}</th>
              <th class="text-end">{{ formatMoney(summary.vat) }}</th>
              <th class="text-end">{{ signedMoney(summary.rounding) }}</th>
              <th class="text-end">{{ formatMoney(summary.total) }}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Taxable / exempt = sales after discount with / without VAT. Total = taxable + exempt + VAT + rounding to the
      invoice's whole amount.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml } from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { COUNT_COLOR } from './salesReportMixin';

const VAT_COLOR = '#13A89E';
const EMPTY_SUMMARY = {
  sub_total: 0, discount: 0, net_of_discount: 0, taxable: 0, exempt: 0, vat: 0, rounding: 0, total: 0,
  invoice_count: 0, rounding_up: 0, rounding_down: 0, discount_rate: 0, vat_rate: 0, taxable_share: 0,
  discounted_count: 0, vat_count: 0, rounded_count: 0, by_rate: [], top_discounted: [],
  show_counts: { all: 0, discounted: 0, vat: 0, exempt: 0, rounded: 0 }, trend_unit: 'day', trend: [],
};

export default {
  name: 'DiscountVatReport',
  mixins: [inventoryReport, periodReport, salesReport],

  reportUrl: apiUrls.discount_vat_report.get,
  fileName: 'discount_vat',
  columns: [
    { key: 'date', label: 'Date' },
    { key: 'invoice_no', label: 'Invoice' },
    { key: 'customer', label: 'Customer' },
    { key: 'sub_total', label: 'Sub Total', class: 'text-end', numeric: true },
    { key: 'discount', label: 'Discount', class: 'text-end', numeric: true },
    { key: 'taxable', label: 'Taxable', class: 'text-end', numeric: true },
    { key: 'exempt', label: 'Exempt', class: 'text-end', numeric: true },
    { key: 'vat', label: 'VAT', class: 'text-end', numeric: true },
    { key: 'rounding', label: 'Rounding', class: 'text-end', numeric: true },
    { key: 'total', label: 'Total', class: 'text-end', numeric: true },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), customer: null, show: null, warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
      COUNT_COLOR,
      VAT_COLOR,
    };
  },

  computed: {
    hasActiveFilters() {
      const { customer, show, search } = this.filters;
      return this.periodChanged || !!(customer || show || search.trim());
    },

    showPills() {
      const counts = this.summary.show_counts;
      return [
        { key: 'all', label: 'All invoices', count: counts.all },
        { key: 'discounted', label: 'Discounted', count: counts.discounted },
        { key: 'vat', label: 'With VAT', count: counts.vat },
        { key: 'exempt', label: 'With exempt sales', count: counts.exempt },
        { key: 'rounded', label: 'Rounded', count: counts.rounded },
      ];
    },

    rateBars() {
      return this.withPercent(this.summary.by_rate, 'base');
    },

    visibleRows() {
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },
  },

  watch: {
    'filters.show'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      return { ...this.periodParams(), ...this.customerParams(), ...(this.filters.show ? { show: this.filters.show } : {}) };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...defaultPeriod(this.today), customer: null, show: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(() => this.drawMoneyTrend(this.$refs.trendChart, [
        { label: 'Discount', field: 'discount', color: COUNT_COLOR },
        { label: 'VAT', field: 'vat', color: VAT_COLOR },
      ]));
    },

    setShow(key) {
      this.filters.show = key === 'all' ? null : key;
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => `
        <tr>
          <td>${this.formatDate(r.date)} ${escapeHtml(r.time)}</td>
          <td>${escapeHtml(r.invoice_no)}</td>
          <td>${escapeHtml(r.customer)}</td>
          <td class="num">${this.formatMoney(r.sub_total)}</td>
          <td class="num">${this.formatMoney(r.discount)}</td>
          <td class="num">${this.formatMoney(r.taxable)}</td>
          <td class="num">${this.formatMoney(r.exempt)}</td>
          <td class="num">${this.formatMoney(r.vat)}</td>
          <td class="num">${this.formatMoney(r.rounding)}</td>
          <td class="num">${this.formatMoney(r.total)}</td>
        </tr>`).join('');
      const table = `<table><thead><tr><th>Date</th><th>Invoice</th><th>Customer</th><th class="num">Sub Total</th>
          <th class="num">Discount</th><th class="num">Taxable</th><th class="num">Exempt</th><th class="num">VAT</th>
          <th class="num">Rounding</th><th class="num">Total</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="3" class="num">Total</td><td class="num">${this.formatMoney(s.sub_total)}</td>
          <td class="num">${this.formatMoney(s.discount)}</td><td class="num">${this.formatMoney(s.taxable)}</td>
          <td class="num">${this.formatMoney(s.exempt)}</td><td class="num">${this.formatMoney(s.vat)}</td>
          <td class="num">${this.formatMoney(s.rounding)}</td><td class="num">${this.formatMoney(s.total)}</td></tr></tfoot></table>`;
      const show = this.showPills.find((p) => p.key === (this.filters.show || 'all'))?.label;
      const search = this.filters.search.trim();
      this.openPrint(
        'Discount & VAT Report',
        [
          [this.periodText(), `Show: ${show}`, this.customerText(), this.storeText(),
            ...(search ? [`Search: ${search}`] : [])].join(' · '),
          `Printed ${new Date().toLocaleString()}`,
        ],
        [
          ['Discount', `${this.formatMoney(s.discount)} (${s.discount_rate}%)`],
          ['Taxable · Exempt', `${this.formatMoney(s.taxable)} · ${this.formatMoney(s.exempt)}`],
          ['VAT', `${this.formatMoney(s.vat)} (${s.vat_rate}%)`],
          ['Rounding', this.signedMoney(s.rounding)],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
